import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { getEnv } from '@/app/conf/env';
import { getTokens, setTokens } from '@/shared/auth/user-store';

import { isTokenExpired, refreshAuthToken } from './auth-token';

axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers['Accept'] = 'application/json';
axios.defaults.headers['X-Firefly-Client'] = 'Web';

/**
 * Interceptor function to add authentication token to Axios request config.
 *
 * @param config - The Axios request config.
 * @returns The updated Axios request config.
 */
async function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const authToken = getTokens();

  if (!authToken) return config;

  config.headers = config.headers ?? {};

  const { access_token, refresh_token } = authToken;
  if (isTokenExpired(access_token) && refresh_token) {
    const response = await refreshAuthToken({ refreshToken: refresh_token });

    if (response) {
      setTokens(response);
      config.headers['Authorization'] = `Bearer ${response.access_token}`;
    }
  } else {
    config.headers['Authorization'] = `Bearer ${access_token}`;
  }

  return config;
}

export const fireflyApi = axios.create({
  baseURL: `${getEnv().FIREFLY_API_URL}/`
});

/**
 * Axios instance for making requests to the Firefly API.
 */
export const fireflyAuthenticatedApi = axios.create({
  baseURL: `${getEnv().FIREFLY_API_URL}/`
});

/**
 * Intercepts a successful Axios response and extracts its data payload.
 */
function successResponseInterceptor<TData, TConfig>(response: AxiosResponse<TData, TConfig>) {
  return response.data;
}

/**
 * Rejects a response interceptor error by returning a rejected Promise.
 */
function rejectResponseInterceptor(error: unknown) {
  return Promise.reject(error);
}

fireflyAuthenticatedApi.interceptors.request.use(authRequestInterceptor, rejectResponseInterceptor);
fireflyAuthenticatedApi.interceptors.response.use(successResponseInterceptor, rejectResponseInterceptor);
fireflyApi.interceptors.response.use(successResponseInterceptor, rejectResponseInterceptor);
